"use client";

import { useState } from "react";
import { useTranslations } from "next-intl";
import { Bug, X, Send, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

type State = "idle" | "open" | "sending" | "success" | "error";

export function BugReportButton() {
  const t = useTranslations("bugReportButton");
  const [state, setState] = useState<State>("idle");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  function open() {
    setState("open");
    setMessage("");
    setEmail("");
    setErrorMsg("");
  }

  function close() {
    if (state === "sending") return;
    setState("idle");
  }

  async function submit() {
    if (message.trim().length < 5) {
      setErrorMsg(t("errMin"));
      return;
    }
    setState("sending");
    try {
      const res = await fetch("/api/bug-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: message.trim(),
          email: email.trim() || undefined,
          page: typeof window !== "undefined" ? window.location.href : undefined,
          meta: typeof window !== "undefined" ? {
            userAgent: navigator.userAgent,
            language: navigator.language,
            screen: `${screen.width}×${screen.height}`,
            referrer: document.referrer || undefined,
          } : undefined,
        }),
      });
      if (!res.ok) throw new Error("server error");
      setState("success");
      setTimeout(() => setState("idle"), 3000);
    } catch {
      setState("error");
      setErrorMsg(t("errSend"));
    }
  }

  return (
    <>
      {/* Floating trigger button */}
      {state === "idle" && (
        <button
          type="button"
          onClick={open}
          title={t("title")}
          className="fixed bottom-24 right-4 lg:bottom-8 lg:right-6 z-[9999] w-10 h-10 rounded-full bg-stone-800/70 hover:bg-stone-800 text-white shadow-lg transition-all flex items-center justify-center backdrop-blur-sm"
        >
          <Bug className="w-4 h-4" />
        </button>
      )}

      {/* Success toast */}
      {state === "success" && (
        <div className="fixed bottom-24 right-4 lg:bottom-8 lg:right-6 z-[9999] flex items-center gap-2 bg-green-700 text-white text-sm px-4 py-2.5 rounded-full shadow-lg animate-fade-in">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {t("thanksSent")}
        </div>
      )}

      {/* Modal overlay */}
      {(state === "open" || state === "sending" || state === "error") && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) close(); }}
        >
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Bug className="w-5 h-5 text-stone-500" />
                <h2 className="text-base font-semibold text-stone-900">{t("title")}</h2>
              </div>
              <button
                onClick={close}
                disabled={state === "sending"}
                className="p-1 rounded-lg text-stone-400 hover:text-stone-600 hover:bg-stone-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Message */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t("whatHappened")} <span className="text-red-400">*</span>
              </label>
              <textarea
                value={message}
                onChange={(e) => { setMessage(e.target.value); setErrorMsg(""); }}
                placeholder={t("messagePlaceholder")}
                rows={4}
                disabled={state === "sending"}
                className="w-full rounded-xl border border-stone-200 px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent resize-none disabled:opacity-60"
              />
            </div>

            {/* Email (optional) */}
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">
                {t("emailLabel")} <span className="text-stone-400 font-normal">{t("optional")}</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@ejemplo.com"
                disabled={state === "sending"}
                className="w-full rounded-xl border border-stone-200 px-3 py-2.5 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent disabled:opacity-60"
              />
            </div>

            {/* Error */}
            {errorMsg && (
              <div className="flex items-center gap-2 text-sm text-red-600">
                <AlertCircle className="w-4 h-4 shrink-0" />
                {errorMsg}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                onClick={close}
                disabled={state === "sending"}
                className="flex-1"
              >
                {t("cancel")}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={submit}
                disabled={state === "sending" || message.trim().length < 5}
                className="flex-1 gap-1.5"
              >
                {state === "sending" ? (
                  <><span className="animate-spin inline-block w-3 h-3 border-2 border-white/30 border-t-white rounded-full" />{t("sending")}</>
                ) : (
                  <><Send className="w-3.5 h-3.5" />{t("send")}</>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
